import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { 
  Smartphone, 
  Gift,
  Trophy,
  Heart,
  Star,
  Check,
  ShoppingCart,
  CreditCard,
  Truck,
  Shield,
  MessageSquare,
  Video,
  Image as ImageIcon,
  Music,
  Package,
  Sparkles,
  Menu,
  X,
  ChevronRight,
  Mail
} from 'lucide-react';

const GiftsPricingPage = () => {
  const { t } = useTranslation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');

  const categories = [
    { id: 'all', name: 'All Products' },
    { id: 'cards', name: 'Cards' },
    { id: 'trophies', name: 'Trophies & Awards' },
    { id: 'keepsakes', name: 'Keepsakes' },
    { id: 'tags', name: 'Tags Only' }
  ];

  const products = [
    {
      id: 'greeting-card',
      category: 'cards',
      name: 'Personalised Greeting Card',
      description: 'Beautiful card with NFC tag or QR code. Recipient taps or scans to see your video, photos, or message.',
      price: 4.99,
      originalPrice: 11.99,
      image: '🎴',
      features: ['Premium card stock', 'NFC tag or QR code sticker', 'Upload video, photos & text', 'Lifetime hosting', 'Gift envelope included'],
      popular: true,
      tag: 'Best Seller'
    },
    {
      id: 'birthday-card',
      category: 'cards',
      name: 'Birthday Celebration Card',
      description: 'Make birthdays unforgettable with a video message. Tap or scan to play.',
      price: 5.99,
      originalPrice: 14.99,
      image: '🎂',
      features: ['Birthday-themed design', 'NFC tag or QR code sticker', 'Upload video message', 'Add photos & music', 'Comes with envelope'],
      popular: false
    },
    {
      id: 'anniversary-card',
      category: 'cards',
      name: 'Anniversary Memory Card',
      description: 'Celebrate your love with photos and videos. Tap or scan to view.',
      price: 5.99,
      originalPrice: 14.99,
      image: '💕',
      features: ['Elegant romantic design', 'NFC tag or QR code sticker', 'Upload multiple videos', 'Photo slideshow', 'Premium envelope'],
      popular: false
    },
    {
      id: 'trophy-small',
      category: 'trophies',
      name: 'Achievement Trophy - Small',
      description: 'Perfect for kids sports, school awards. Tap or scan to view memories.',
      price: 24.99,
      originalPrice: 29.99,
      image: '🏆',
      features: ['6" height', 'NFC tag or QR code sticker', 'Upload ceremony video', 'Add winner photos', 'Engraving available'],
      popular: false
    },
    {
      id: 'trophy-medium',
      category: 'trophies',
      name: 'Achievement Trophy - Medium',
      description: 'Ideal for corporate awards and sports achievements.',
      price: 39.99,
      originalPrice: 49.99,
      image: '🏆',
      features: ['10" height', 'NFC tag or QR code sticker', 'Upload highlight reel', 'Multiple photos', 'Custom engraving included'],
      popular: true,
      tag: 'Popular'
    },
    {
      id: 'medal',
      category: 'trophies',
      name: 'Winner\'s Medal',
      description: 'Medal that plays the winning moment. Tap or scan to view.',
      price: 19.99,
      originalPrice: 24.99,
      image: '🥇',
      features: ['Premium metal finish', 'NFC tag or QR code sticker', 'Upload victory video', 'Ribbon included', 'Presentation box'],
      popular: false
    },
    {
      id: 'photo-frame',
      category: 'keepsakes',
      name: 'Interactive Photo Frame',
      description: 'Display a photo that comes to life with video when tapped or scanned.',
      price: 29.99,
      originalPrice: 34.99,
      image: '🖼️',
      features: ['5x7" frame size', 'NFC tag or QR code sticker', 'Static photo + video', 'Desktop or wall mount', 'Premium wood finish'],
      popular: false
    },
    {
      id: 'keychain',
      category: 'keepsakes',
      name: 'Memory Keychain',
      description: 'Carry your memories everywhere. Tap or scan to relive special moments.',
      price: 9.99,
      originalPrice: 12.99,
      image: '🔑',
      features: ['Durable metal design', 'NFC tag or QR code sticker', 'Upload photos & video', 'Compact size', 'Gift box included'],
      popular: false
    },
    {
      id: 'ornament',
      category: 'keepsakes',
      name: 'Christmas Memory Ornament',
      description: 'An ornament that plays family holiday videos year after year.',
      price: 16.99,
      originalPrice: 19.99,
      image: '🎄',
      features: ['Festive design', 'NFC tag or QR code sticker', 'Upload holiday videos', 'Add family photos', 'Ribbon hanger'],
      popular: false
    },
    {
      id: 'single-tag',
      category: 'tags',
      name: 'Single NFC Tag or QR Sticker',
      description: 'Add to any object you already have. Choose NFC tag or QR code sticker.',
      price: 4.99,
      originalPrice: 6.99,
      image: '📍',
      features: ['Adhesive backing', 'Choose NFC or QR', 'Waterproof', 'Works with any phone', '+$1.00 for metal surface'],
      popular: false
    },
    {
      id: 'tag-3pack',
      category: 'tags',
      name: 'Tags or Stickers - 3 Pack',
      description: 'Great for multiple gifts or projects. Choose NFC tags or QR stickers.',
      price: 11.99,
      originalPrice: 17.99,
      image: '📍',
      features: ['3 tags or stickers', 'Choose NFC or QR', 'Waterproof', 'Individual upload pages', '+$0.50 each for metal surface'],
      popular: true,
      tag: 'Best Value'
    },
    {
      id: 'tag-10pack',
      category: 'tags',
      name: 'Tags or Stickers - 10 Pack',
      description: 'For creative projects or gifting multiple items. Bulk savings.',
      price: 29.99,
      originalPrice: 49.99,
      image: '📍',
      features: ['10 tags or stickers', 'Choose NFC or QR', 'Waterproof', 'Individual upload pages', '+$0.20 each for metal surface'],
      popular: false
    }
  ];

  const filteredProducts = selectedCategory === 'all' 
    ? products 
    : products.filter(p => p.category === selectedCategory);

  const howItWorks = [
    { icon: <ShoppingCart className="w-6 h-6" />, title: 'Order Your Product', description: 'Choose from cards, trophies, keepsakes, or sticker tags' },
    { icon: <Video className="w-6 h-6" />, title: 'Upload Your Content', description: 'Add videos, photos, and messages via your unique link' },
    { icon: <Gift className="w-6 h-6" />, title: 'Give the Gift', description: 'Recipient taps NFC or scans QR code to see your message' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-pink-50 via-white to-purple-50">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-lg border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-10 h-10 bg-gradient-to-br from-pink-500 to-purple-600 rounded-xl flex items-center justify-center">
                <Gift className="w-5 h-5 text-white" />
              </div>
              <div>
                <span className="font-bold text-xl text-gray-900">ScanIt2Go</span>
                <span className="text-xs text-pink-600 block -mt-1">Gifts & Awards</span>
              </div>
            </Link>

            <div className="hidden md:flex items-center gap-8">
              <Link to="/" className="text-gray-600 hover:text-gray-900 transition-colors">{t('marketing.nav.features')}</Link>
              <Link to="/#solutions" className="text-gray-600 hover:text-gray-900 transition-colors">{t('marketing.nav.solutions')}</Link>
              <a href="#products" className="text-pink-600 font-medium">{t('giftsPricing.products.title')}</a>
              <Link to="/faq" className="text-gray-600 hover:text-gray-900 transition-colors">{t('marketing.nav.faq')}</Link>
            </div>

            <div className="hidden md:flex items-center gap-4">
              <a href="#products" className="px-5 py-2.5 bg-gradient-to-r from-pink-500 to-purple-600 text-white rounded-full font-medium hover:opacity-90 transition-opacity">
                {t('giftsPricing.orderNow')}
              </a>
            </div>

            <button className="md:hidden p-2" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {mobileMenuOpen && (
          <motion.div 
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            className="md:hidden bg-white border-t border-gray-100"
          >
            <div className="px-6 py-4 space-y-4">
              <Link to="/" className="block text-gray-600">{t('marketing.nav.features')}</Link>
              <Link to="/#solutions" className="block text-gray-600">{t('marketing.nav.solutions')}</Link>
              <a href="#products" className="block text-pink-600 font-medium">{t('giftsPricing.products.title')}</a>
              <Link to="/faq" className="block text-gray-600">{t('marketing.nav.faq')}</Link>
              <hr className="border-gray-100" />
              <a href="#products" className="block w-full text-center px-5 py-2.5 bg-gradient-to-r from-pink-500 to-purple-600 text-white rounded-full font-medium">
                {t('giftsPricing.orderNow')}
              </a>
            </div>
          </motion.div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="pt-28 pb-16 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-pink-100 rounded-full text-pink-700 text-sm font-medium mb-6">
              <Sparkles className="w-4 h-4" />
              {t('giftsPricing.badge')}
            </div>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight mb-6">
              {t('giftsPricing.title')}
            </h1>
            
            <p className="text-lg sm:text-xl text-gray-600 leading-relaxed mb-8 max-w-2xl mx-auto">
              {t('giftsPricing.subtitle')}
            </p>

            <div className="flex flex-wrap justify-center gap-4">
              <a 
                href="#products"
                className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-pink-500 to-purple-600 text-white rounded-full font-semibold hover:opacity-90 transition-all shadow-lg"
              >
                {t('giftsPricing.products.title')}
                <ChevronRight className="w-5 h-5" />
              </a>
              <a 
                href="#how-it-works"
                className="inline-flex items-center gap-2 px-8 py-4 bg-white text-gray-700 rounded-full font-semibold hover:bg-gray-50 transition-all border border-gray-200"
              >
                {t('giftsPricing.howItWorks')}
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Trust Badges */}
      <section className="py-8 px-6 border-y border-gray-100 bg-white/50">
        <div className="max-w-5xl mx-auto">
          <div className="flex flex-wrap justify-center gap-8 text-sm text-gray-600">
            <div className="flex items-center gap-2">
              <Truck className="w-5 h-5 text-pink-500" />
              <span>Free Shipping</span>
            </div>
            <div className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-pink-500" />
              <span>Secure Payment</span>
            </div>
            <div className="flex items-center gap-2">
              <Heart className="w-5 h-5 text-pink-500" />
              <span>Lifetime Hosting</span>
            </div>
            <div className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-pink-500" />
              <span>Customer Support</span>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-20 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">How It Works</h2>
            <p className="text-gray-600">Three simple steps to create an unforgettable gift</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {howItWorks.map((step, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="text-center"
              >
                <div className="w-16 h-16 bg-gradient-to-br from-pink-100 to-purple-100 rounded-2xl flex items-center justify-center text-pink-600 mx-auto mb-4">
                  {step.icon}
                </div>
                <div className="w-8 h-8 bg-pink-500 text-white rounded-full flex items-center justify-center font-bold text-sm mx-auto mb-4">
                  {i + 1}
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{step.title}</h3>
                <p className="text-gray-600">{step.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section id="products" className="py-20 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Choose Your Perfect Gift</h2>
            <p className="text-gray-600 mb-8">All products include your choice of NFC tag or QR code sticker and lifetime content hosting</p>

            {/* Category Filter */}
            <div className="flex flex-wrap justify-center gap-2 mb-8">
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => setSelectedCategory(cat.id)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                    selectedCategory === cat.id
                      ? 'bg-gradient-to-r from-pink-500 to-purple-600 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                  data-testid={`category-${cat.id}`}
                >
                  {cat.name}
                </button>
              ))}
            </div>
          </div>

          {/* Product Grid */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredProducts.map((product, i) => (
              <motion.div
                key={product.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
                className="bg-white rounded-2xl border border-gray-200 overflow-hidden hover:shadow-xl transition-all group"
                data-testid={`product-${product.id}`}
              >
                {/* Product Image/Emoji */}
                <div className="relative bg-gradient-to-br from-pink-50 to-purple-50 p-8 text-center">
                  <span className="text-6xl">{product.image}</span>
                  {product.tag && (
                    <span className="absolute top-3 right-3 px-3 py-1 bg-gradient-to-r from-pink-500 to-purple-600 text-white text-xs font-semibold rounded-full">
                      {product.tag}
                    </span>
                  )}
                </div>

                {/* Product Info */}
                <div className="p-5">
                  <h3 className="font-semibold text-gray-900 mb-1">{product.name}</h3>
                  <p className="text-sm text-gray-500 mb-4 line-clamp-2">{product.description}</p>

                  {/* Price */}
                  <div className="flex items-baseline gap-2 mb-4">
                    <span className="text-2xl font-bold text-gray-900">${product.price}</span>
                    {product.originalPrice && (
                      <span className="text-sm text-gray-400 line-through">${product.originalPrice}</span>
                    )}
                  </div>

                  {/* Features */}
                  <ul className="space-y-1.5 mb-4">
                    {product.features.slice(0, 3).map((feature, j) => (
                      <li key={j} className="flex items-center gap-2 text-xs text-gray-600">
                        <Check className="w-3.5 h-3.5 text-pink-500 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>

                  {/* CTA */}
                  <a
                    href={`mailto:andy@scanit2go.com?subject=Order: ${product.name}&body=Hi, I would like to order: ${product.name} ($${product.price})`}
                    className="block w-full py-3 bg-gradient-to-r from-pink-500 to-purple-600 text-white text-center rounded-xl font-medium hover:opacity-90 transition-opacity"
                  >
                    Order Now
                  </a>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* What You Can Upload */}
      <section className="py-20 px-6 bg-gradient-to-br from-pink-50 to-purple-50">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Upload Any Type of Content</h2>
            <p className="text-gray-600">Make your gift as personal as you want</p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: <Video className="w-8 h-8" />, title: 'Video Messages', desc: 'Record a heartfelt video' },
              { icon: <ImageIcon className="w-8 h-8" />, title: 'Photos', desc: 'Add multiple photos' },
              { icon: <MessageSquare className="w-8 h-8" />, title: 'Text Messages', desc: 'Write a personal note' },
              { icon: <Music className="w-8 h-8" />, title: 'Audio', desc: 'Add voice recordings' }
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-white rounded-2xl p-6 text-center shadow-sm"
              >
                <div className="w-16 h-16 bg-gradient-to-br from-pink-100 to-purple-100 rounded-2xl flex items-center justify-center text-pink-600 mx-auto mb-4">
                  {item.icon}
                </div>
                <h3 className="font-semibold text-gray-900 mb-1">{item.title}</h3>
                <p className="text-sm text-gray-500">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Teaser */}
      <section className="py-20 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Have Questions?</h2>
          <p className="text-gray-600 mb-8">
            Check our FAQ for answers about shipping, uploading content, and how the technology works.
          </p>
          <Link 
            to="/faq"
            className="inline-flex items-center gap-2 px-6 py-3 bg-white border border-gray-200 rounded-full font-medium text-gray-700 hover:bg-gray-50 transition-colors"
          >
            View FAQ
            <ChevronRight className="w-5 h-5" />
          </Link>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-20 px-6 bg-gradient-to-r from-pink-500 to-purple-600">
        <div className="max-w-4xl mx-auto text-center text-white">
          <h2 className="text-3xl font-bold mb-4">Need a Custom Order?</h2>
          <p className="text-pink-100 mb-8">
            Planning a wedding, corporate event, or bulk order? Get in touch for custom pricing.
          </p>
          <a 
            href="mailto:andy@scanit2go.com?subject=Custom Order Inquiry"
            className="inline-flex items-center gap-2 px-8 py-4 bg-white text-pink-600 rounded-full font-semibold hover:bg-gray-100 transition-colors"
          >
            <Mail className="w-5 h-5" />
            Contact Us
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-16 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-12 mb-12">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-pink-500 to-purple-500 rounded-xl flex items-center justify-center">
                  <Gift className="w-5 h-5 text-white" />
                </div>
                <div>
                  <span className="font-bold text-xl">ScanIt2Go</span>
                  <span className="text-xs text-pink-400 block -mt-1">Gifts & Awards</span>
                </div>
              </div>
              <p className="text-gray-400">
                Turn ordinary gifts into extraordinary memories with NFC technology.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Products</h4>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#products" className="hover:text-white transition-colors">Greeting Cards</a></li>
                <li><a href="#products" className="hover:text-white transition-colors">Trophies & Awards</a></li>
                <li><a href="#products" className="hover:text-white transition-colors">Keepsakes</a></li>
                <li><a href="#products" className="hover:text-white transition-colors">NFC Tags</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Support</h4>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/faq" className="hover:text-white transition-colors">FAQ</Link></li>
                <li><Link to="/#contact" className="hover:text-white transition-colors">Contact Us</Link></li>
                <li><Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Contact</h4>
              <ul className="space-y-2 text-gray-400">
                <li>andy@scanit2go.com</li>
                <li>www.scanit2go.com</li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-gray-400">© 2026 ScanIt2Go. All rights reserved.</p>
            <div className="flex items-center gap-4">
              <Link to="/" className="text-gray-400 hover:text-white transition-colors">Business Solutions</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default GiftsPricingPage;
